import React, { useState } from 'react';
import jsYaml from 'js-yaml';

// Define types for our component
interface YamlToXmlProps {}

// XML formatting options
type XmlFormat = 'pretty' | 'compact';

// Function to convert JSON object to XML string
const jsonToXml = (obj: any, rootName: string = 'root', indent: number = 2, level: number = 0): string => {
  // Helper function to create indentation
  const getIndent = (level: number): string => {
    return ' '.repeat(level * indent);
  };

  // Function to process array items
  const processArray = (array: any[], itemName: string = 'item', currentLevel: number): string => {
    return array.map(item => {
      if (typeof item === 'object' && item !== null) {
        // For objects in arrays, use the item name as the tag
        return `${getIndent(currentLevel)}<${itemName}>\n${jsonToXml(item, itemName, indent, currentLevel + 1)}${getIndent(currentLevel)}</${itemName}>`;
      } else {
        // For primitive values in arrays
        return `${getIndent(currentLevel)}<${itemName}>${escapeXml(String(item))}</${itemName}>`;
      }
    }).join('\n');
  };

  // Escape XML special characters
  const escapeXml = (str: string): string => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  // Start XML conversion
  let xml = '';

  if (Array.isArray(obj)) {
    // Handle array at the root level
    return processArray(obj, 'item', level);
  } else if (typeof obj === 'object' && obj !== null) {
    // Handle object properties
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        const sanitizedKey = key.replace(/[^a-zA-Z0-9_-]/g, '_'); // Ensure valid XML tag names

        if (Array.isArray(value)) {
          // Handle arrays
          xml += `${getIndent(level)}<${sanitizedKey}>\n`;
          xml += processArray(value, 'item', level + 1);
          xml += `\n${getIndent(level)}</${sanitizedKey}>\n`;
        } else if (typeof value === 'object' && value !== null) {
          // Handle nested objects
          xml += `${getIndent(level)}<${sanitizedKey}>\n`;
          xml += jsonToXml(value, sanitizedKey, indent, level + 1);
          xml += `${getIndent(level)}</${sanitizedKey}>\n`;
        } else {
          // Handle primitive values
          xml += `${getIndent(level)}<${sanitizedKey}>${escapeXml(String(value))}</${sanitizedKey}>\n`;
        }
      }
    }
    return xml;
  } else {
    // Handle primitive root (unlikely but possible)
    return `${escapeXml(String(obj))}`;
  }
};

const YamlToXml: React.FC<YamlToXmlProps> = () => {
  // State for the YAML input
  const [yamlInput, setYamlInput] = useState<string>('');
  // State for the XML output
  const [xmlOutput, setXmlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('yaml_data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for XML format
  const [xmlFormat, setXmlFormat] = useState<XmlFormat>('pretty');
  // State for indentation level (only applicable for 'pretty' format)
  const [indentLevel, setIndentLevel] = useState<number>(2);
  // State for root element name
  const [rootElement, setRootElement] = useState<string>('root');
  // State to track if the conversion happened
  const [isXmlGenerated, setIsXmlGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);

  // Handle YAML input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setYamlInput(e.target.value);
    setError('');
    setIsXmlGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Handle root element name change
  const handleRootElementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRootElement(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    const exampleYaml = `# Example YAML data
version: '3'
services:
  webapp:
    image: nginx:latest
    ports:
      - "80:80"
    environment:
      - NODE_ENV=production
      - DEBUG=false
    volumes:
      - ./app:/app
      - ./config:/etc/nginx/conf.d
    depends_on:
      - database
  database:
    image: postgres:13
    environment:
      POSTGRES_USER: user
      POSTGRES_PASSWORD: password
      POSTGRES_DB: mydb
    volumes:
      - db-data:/var/lib/postgresql/data
volumes:
  db-data:
    driver: local`;

    setYamlInput(exampleYaml);
    setError('');
    setIsXmlGenerated(false);
    setXmlOutput('');
  };

  // Validate YAML
  const validateYaml = (yamlString: string): [boolean, any] => {
    try {
      const parsed = jsYaml.load(yamlString);
      return [true, parsed];
    } catch (e) {
      return [false, e];
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setYamlInput('');
    setXmlOutput('');
    setError('');
    setFileName('yaml_data');
    setXmlFormat('pretty');
    setIndentLevel(2);
    setRootElement('root');
    setIsXmlGenerated(false);
  };

  // Beautify/Format YAML
  const beautifyYaml = () => {
    try {
      if (!yamlInput.trim()) {
        setError('Please enter YAML data to format');
        return;
      }
      
      const [isValid, parsed] = validateYaml(yamlInput);
      
      if (!isValid) {
        setError(`Invalid YAML format: ${parsed instanceof Error ? parsed.message : String(parsed)}`);
        return;
      }
      
      // Convert back to YAML with consistent formatting
      const beautified = jsYaml.dump(parsed, {
        indent: 2,
        lineWidth: -1,
        noRefs: true,
        noCompatMode: true
      });
      
      setYamlInput(beautified);
      setError('');
    } catch (e) {
      setError(`Error formatting YAML: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Convert YAML to XML
  const convertYamlToXml = () => {
    setError('');
    setIsLoading(true);
    setIsXmlGenerated(false);
    setXmlOutput('');
    
    if (!yamlInput.trim()) {
      setError('Please enter YAML data');
      setIsLoading(false);
      return;
    }

    try {
      const [isValid, parsedYaml] = validateYaml(yamlInput);
      
      if (!isValid) {
        setError(`Invalid YAML format: ${parsedYaml instanceof Error ? parsedYaml.message : String(parsedYaml)}`);
        setIsLoading(false);
        return;
      }
      
      // Create XML declaration
      let xmlString = '<?xml version="1.0" encoding="UTF-8"?>\n';
      
      // Add root element
      xmlString += `<${rootElement}>\n`;
      
      // Convert YAML to XML
      if (xmlFormat === 'pretty') {
        xmlString += jsonToXml(parsedYaml, rootElement, indentLevel, 1);
      } else {
        // For compact format, we'll use indentation of 0
        xmlString += jsonToXml(parsedYaml, rootElement, 0, 1).replace(/\n/g, '');
      }
      
      // Close root element
      if (xmlFormat === 'pretty') {
        xmlString += `</${rootElement}>`;
      } else {
        xmlString += `</${rootElement}>`;
      }
      
      setXmlOutput(xmlString);
      setIsXmlGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting YAML to XML: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy XML to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(xmlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download XML file
  const downloadXml = () => {
    const blob = new Blob([xmlOutput], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'yaml_data'}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">YAML to XML Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert YAML data to XML format. Paste your YAML data below to generate valid XML output.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="yamlInput" className="font-medium text-gray-700">
                YAML Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyYaml}
                >
                  Format YAML
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setYamlInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="yamlInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={yamlInput}
              onChange={handleInputChange}
              placeholder="Paste your YAML data here, e.g.,
name: John
age: 30
address:
  city: New York
  zip: 10001
hobbies:
  - reading
  - hiking"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="rootElement" className="block text-sm font-medium text-gray-700 mb-1">
                    Root Element Name
                  </label>
                  <input
                    type="text"
                    id="rootElement"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={rootElement}
                    onChange={handleRootElementChange}
                    placeholder="Enter root element name"
                  />
                </div>
                
                <div>
                  <label htmlFor="xmlFormat" className="block text-sm font-medium text-gray-700 mb-1">
                    XML Format
                  </label>
                  <select
                    id="xmlFormat"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={xmlFormat}
                    onChange={(e) => setXmlFormat(e.target.value as XmlFormat)}
                  >
                    <option value="pretty">Pretty (indented with line breaks)</option>
                    <option value="compact">Compact (minimal whitespace)</option>
                  </select>
                </div>
                
                {xmlFormat === 'pretty' && (
                  <div>
                    <label htmlFor="indentLevel" className="block text-sm font-medium text-gray-700 mb-1">
                      Indentation Level
                    </label>
                    <div className="flex items-center">
                      <input
                        type="range"
                        id="indentLevel"
                        min="1"
                        max="8"
                        value={indentLevel}
                        onChange={(e) => setIndentLevel(parseInt(e.target.value))}
                        className="w-4/5 mr-2"
                      />
                      <span className="text-sm text-gray-700">{indentLevel} spaces</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertYamlToXml}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to XML'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              XML Output
            </label>
            
            {isXmlGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy XML'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadXml}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isXmlGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    XML output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isXmlGenerated && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="xmlOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={xmlOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isXmlGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">YAML Successfully Converted to XML!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated XML for use in your applications.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About YAML to XML Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your YAML data into valid XML format. YAML (YAML Ain't Markup Language) is a human-friendly data serialization
              standard, while XML (eXtensible Markup Language) is widely used for data interchange in various applications, particularly for configuration files and web services.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Accurate Conversion</strong> - Preserves hierarchical structure and nested elements</li>
              <li><strong>Custom Root Element</strong> - Specify your own root element name</li>
              <li><strong>Format Options</strong> - Choose between pretty-printed or compact XML output</li>
              <li><strong>Customizable Indentation</strong> - Set your preferred indentation level for better readability</li>
              <li><strong>Error Handling</strong> - Clear error messages for invalid YAML input</li>
              <li><strong>Easy Download</strong> - Save the converted XML as a file with one click</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format YAML" button to check if your YAML is valid before conversion</li>
              <li>XML element names cannot contain spaces or special characters - these will be converted to underscores</li>
              <li>For large data sets, the "Compact" format may be more efficient but harder to read</li>
              <li>YAML is indentation-sensitive - make sure your input has consistent spacing</li>
              <li>Arrays in YAML will be converted to repeated elements in XML</li>
              <li>Use the example button to see a properly formatted YAML example</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">XML Special Characters</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Special characters like &lt;, &gt;, &, ', and " are automatically escaped in XML output</li>
              <li>Valid XML cannot start with certain characters or contain invalid characters in element names</li>
              <li>The converter automatically sanitizes element names to ensure XML validity</li>
              <li>XML has strict syntax requirements that YAML doesn't have - the converter handles these differences</li>
              <li>The XML declaration ?xml version="1.0" encoding="UTF-8"? is automatically added to the output</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YamlToXml;